import {
  Assessment,
  AssessmentMethodType,
  FilterKeys,
  GET_ASSESSMENTS_AVERAGE_QUERY,
  useGetAssessmentGradeQuery,
  useGetAssessmentsEntriesQuery,
  useGetGroupDetailsQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useNotifications } from '@schooly/components/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { useAssessmentsAverage } from '../../Markbook/common/useAssessmentsAverage';
import { MarkbookViewSettings } from '../../Markbook/MarkbookSettings/MarkbookSettings';

type UseAssessmentForGroupProps = {
  assessment: Assessment;
  groupId: string;
  settings: MarkbookViewSettings;
};

export const useAssessmentEntriesForGroup = ({
  assessment,
  groupId,
  settings,
}: UseAssessmentForGroupProps) => {
  const { showError } = useNotifications();
  const { schoolId = '' } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: groupResponse,
    isLoading: isGroupLoading,
    isFetching: isGroupFetching,
  } = useGetGroupDetailsQuery(
    {
      id: groupId,
      date: assessment.assessment_date,
    },
    { onError: showError, refetchOnMount: 'always' },
  );

  const group = groupResponse ? groupResponse.group : undefined;

  const { data, isLoading, setParams } = useGetAssessmentsEntriesQuery(
    {
      schoolId,
      filters: {
        [FilterKeys.Assessment]: [assessment.id],
        [FilterKeys.Group]: [groupId],
      },
    },
    { enabled: !isGroupLoading && !isGroupFetching && !!schoolId, onError: showError },
  );

  const assessmentForGroup = data?.pages[0].results[0];

  const assessmentMethodId =
    assessment?.methods.find((method) => method.method_type === AssessmentMethodType.Grade)
      ?.select_list_id ?? '';

  const { data: assessmentGrade, isInitialLoading: isLoadingGrade } = useGetAssessmentGradeQuery(
    assessmentMethodId,
    {
      enabled: !!assessmentMethodId,
    },
  );

  const {
    averageData,
    subjectAverageData,
    isAverageLoading,
    setBenchmarkEnabled,
    setBenchmarkAverage,
    setBenchmarkSubjectAverage,
    benchmarkEnabled,
  } = useAssessmentsAverage({
    requestParams: {
      query: { assessment_ids: assessment.id, group_ids: groupId },
      params: { schoolId: schoolId ?? '' },
    },
    settings,
    canFetchAverage: Boolean(schoolId && (settings.avgResults || settings.coloredCells)),
  });

  const handleSuccessfulEntryUpdate = useCallback(() => {
    queryClient.invalidateQueries([GET_ASSESSMENTS_AVERAGE_QUERY]);
  }, [queryClient]);

  useEffect(() => {
    setParams((prev) => ({ ...prev, filters: { ...prev.filters, [FilterKeys.Group]: [groupId] } }));
  }, [groupId, setParams]);

  return {
    group,
    isGroupLoading,
    isEntriesLoading: isLoading,
    assessmentForGroup,
    assessmentGrade,
    averageData,
    subjectAverageData,
    setBenchmarkSubjectAverage,
    isAverageLoading,
    handleSuccessfulEntryUpdate,
    isLoadingGrade: !!assessmentMethodId && isLoadingGrade,
    setBenchmarkEnabled,
    setBenchmarkAverage,
    benchmarkEnabled,
  };
};
